import { EcoaContent } from "@components/common/EconContent";
import { FontController } from "@components/common/FontController";
import { getBrowserDefaultLanguage, LOCALE_LANG_KEY, sessionSlice } from "@maven-rest/states/reducers/session";
import { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { LoLang } from "maven-lib/dist/consts";
import { useDispatch } from "react-redux";
import { FxGuard } from "@coralblack/flax";
import { useLangContext } from "@layouts/home/LangContext";
import { StudySpec } from "maven-lib/dist/consts/edc";
import { getCrfLabel } from "maven-lib/dist/consts/edc/lib";
import { EcoaVisit, EcoaVisitStatus } from "maven-lib/dist/dto/entity/ecoaVisit";
import { ecoaVisitApi } from "maven-lib/dist/states/apis/ecoaVisit";
import { readableEcoaVisitStatus } from "maven-lib/dist/utils/converter";
import { CrDialog } from "@maven-surface/components/base/CrDialog";
import { HbList } from "@maven-surface/components/base/HbList";
import { HbListItemProps } from "@maven-surface/components/base/HbListItem";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { MvnMessage } from "@maven-msg/lib/Message";

interface VisitsProps {
  studySpec: StudySpec;
}

type CompletePopupVisit = { visits: EcoaVisit[]; title: string; text: string };

interface StyledListWrapperProps {
  showFontController?: boolean;
}

const StyledVisitsWrapper = styled.div`
  height: calc(100vh - 50px);
  overflow: auto;

  .app-hybrid-content {
    height: 100%;
  }

  .app-hybrid-content-wrapper {
    height: 100%;
    padding-bottom: 0;
  }
`;

const StyledListWrapper = styled.div<StyledListWrapperProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ showFontController }) => (showFontController ? "84px" : "22px")};
  height: 100%;
`;

const StyledPrivacyPolicyLinkWrapper = styled.div`
  text-align: center;
`;

const StyledPrivacyPolicyLink = styled.a`
  display: inline-block;
  padding: 12px 20px;
  margin-bottom: 2rem;
  color: #000;
  text-decoration: underline;

  &:hover {
    color: #000;
  }
`;

export function Visits({ studySpec }: VisitsProps) {
  const { pushPath } = useRoute();
  const dispatch = useDispatch();
  const { fontSize } = useLangContext();
  const { setFontSize } = useLangContext();

  const [isShowPopup, setIsShowPopup] = useState(!!studySpec.config?.ecoa?.completePopup);
  const localStorageLanguage = localStorage.getItem(LOCALE_LANG_KEY) as LoLang;
  const lang = localStorageLanguage || getBrowserDefaultLanguage();

  const getVisitElements = (visits: EcoaVisit[], excludeCrfs?: boolean): Array<HbListItemProps & { key: string | number }> =>
    visits.map((visit, visitIdx) => ({
      key: visitIdx,
      elements: [
        { type: "TEXT", text: getCrfLabel(visit.label) },
        {
          type: "CHIP",
          text:
            excludeCrfs || !visit.status || visit.status === EcoaVisitStatus.NotAvailable ? null : readableEcoaVisitStatus(visit.status),
          color:
            visit.status === EcoaVisitStatus.Done
              ? "BLACK"
              : visit.status === EcoaVisitStatus.Ongoing
              ? "WARNING"
              : visit.status === EcoaVisitStatus.NotDone
              ? "PRIMARY"
              : "FADE-OUT",
        },
      ],
      accessory: "DISCLOSURE",
      onClick:
        !visit.status || visit.status === EcoaVisitStatus.NotAvailable
          ? false
          : () => {
              const { folderId, visitId, visitSeq, crfPageId, crfPageSeq, status } = visit;
              if (excludeCrfs) {
                pushPath(`/pat/crfs/${folderId}/${visitId}/${visitSeq}`);
              } else {
                pushPath(
                  `/pat/visits/${folderId}/${visitId}/${visitSeq}/${crfPageId}/${crfPageSeq}${
                    status === EcoaVisitStatus.NotDone ? "" : "/1"
                  }`
                );
              }
            },
    }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const showFontController = useMemo(() => !!studySpec.config?.ecoa?.settings?.showFontController, []);

  useEffect(() => {
    if (!showFontController) {
      setFontSize("SMALL");
    }
  }, [setFontSize, showFontController]);

  return (
    <StyledVisitsWrapper>
      <EcoaContent fontSize={fontSize}>
        <FxGuard
          api={{ ...ecoaVisitApi.getEcoaVisits }}
          render={(resp) => {
            if (!resp?.visits.length) return <></>;

            const completedPopup =
              isShowPopup &&
              !resp.excludeCrfs &&
              resp.visits.reduce<CompletePopupVisit>(
                (arr, visit) => {
                  const isTarget = studySpec.config?.ecoa?.completePopup?.find(({ target }) =>
                    target.find(
                      (v) =>
                        v.folderId === visit.folderId &&
                        v.visitId === visit.visitId &&
                        v.visitSeq === visit.visitSeq &&
                        v.crfPageId === visit.crfPageId &&
                        v.crfPageSeq === visit.crfPageSeq
                    )
                  );

                  if (isTarget) {
                    arr.visits.push(visit);
                    arr.title = isTarget.title || "";
                    arr.text = isTarget.text;
                  }

                  return arr;
                },
                { visits: [], title: "", text: "" }
              );

            return (
              <>
                {completedPopup && completedPopup.visits.length > 0 && (
                  <CrDialog
                    title={completedPopup.title}
                    onConfirm={() => {
                      setIsShowPopup(false);
                    }}
                    visibility={completedPopup.visits.every((visit) => visit.status === EcoaVisitStatus.Done)}
                    confirm={MvnMessage(COMMON_LANG.CONFIRM_ALTER)}
                  >
                    {completedPopup.text}
                  </CrDialog>
                )}
                <StyledListWrapper showFontController={showFontController}>
                  <HbList
                    title={resp.visitsTitle || "Visits"}
                    items={getVisitElements(resp.visits, resp.excludeCrfs)}
                    fontSize={fontSize}
                  />
                  <StyledPrivacyPolicyLinkWrapper>
                    <StyledPrivacyPolicyLink target="_blank" href={getPrivacyPolicyUrl(lang)}>
                      Privacy Policy
                    </StyledPrivacyPolicyLink>
                  </StyledPrivacyPolicyLinkWrapper>
                </StyledListWrapper>
              </>
            );
          }}
          error={(error) => {
            if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
              dispatch(sessionSlice.actions.signOut());

              return error?.message || "An error has occurred.";
            }
          }}
        />
      </EcoaContent>

      {showFontController && <FontController lang={localStorageLanguage} />}
    </StyledVisitsWrapper>
  );
}

function getPrivacyPolicyUrl(lang: LoLang): string {
  const KO_PRIVACY_POLICY_URL = "https://mvn.do/legal/privacy";
  const EN_PRIVACY_POLICY_URL = "https://mvn.do/legal/privacy/en";

  if (lang === LoLang.KO) return KO_PRIVACY_POLICY_URL;
  return EN_PRIVACY_POLICY_URL;
}
